import styled from "@emotion/styled";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import Cookies from "universal-cookie";
import { getWithQueryApiServices } from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { companyFormInitVal } from "../../../constants";
import { SplitContainer, StyledHeader } from "../../../styles/commonStyles";
import { FullFeaturedCrudGrid } from "../../shared/CommonTable";
import { ParentPageWrapper } from "../../shared/ParentPageWrapper";
import { SubscriptionForm } from "./SubscriptionForm";
import { useUserRole } from "../../../hooks/useUserRole";

const BoxContainer = styled(Box)(({ theme }) => ({
  padding: "1%",
  boxSizing: "border-box",
}));

const ParentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

export const Subscription = () => {
  const outletContext = useOutletContext();
  const { currentCompany, currentTenant, tenantString, fullCompanyList } =
    outletContext;

  const {
    isCurrentUserSysAdmin,
    isGlobalSuperAdmin,
    isLoading: roleLoading,
  } = useUserRole();

  const handleGlobalCompanyChange = (e) => {
    const { setCurrentCompany } = outletContext;
    if (setCurrentCompany) setCurrentCompany(e.target.value);
  };

  const cookies = new Cookies();
  const userID = cookies.get("userId");

  const [companyForm, setCompanyForm] = useState([]);
  const [searchCategory, setSearchCategory] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryData, setCategoryData] = useState({});
  const [addNewCategory, setAddNewCategory] = useState(false);
  const [permission, setPermission] = useState(false);

  // ✅ Add state for selected subscription ID
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);

  const columns = [
    {
      field: "subscriptionID",
      headerName: "ID",
      align: "left",
      headerAlign: "left",
      editable: false,
      flex: 0.3,
    },
    {
      field: "name",
      headerName: "Name",
      align: "left",
      headerAlign: "left",
      editable: false,
      flex: 1,
    },
    {
      field: "title",
      headerName: "Title",
      align: "left",
      headerAlign: "left",
      editable: false,
      flex: 1,
    },
    {
      field: "amount",
      headerName: "Subscription Amount",
      align: "left",
      headerAlign: "left",
      editable: false,
      flex: 0.5,
      type: "number",
    },
  ];

  // ✅ Fetch users data
  const { data: usersData } = useQuery({
    queryKey: ["subscriptionList", currentCompany, tenantString],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_USERS_LIST,
        `pageNumber=1&pageSize=10000&selectedCompany=${currentCompany}${tenantString}`,
      ),
    enabled: !!currentCompany,
  });

  // ✅ Fetch company data
  const { data: companyData, refetch: companyListRefetch } = useQuery({
    queryKey: ["companyLists"],
    queryFn: () =>
      getWithQueryApiServices(apiRoutes.GET_COMPANY_LIST, `pageNumber=1`),
  });

  // ✅ Fetch ALL subscriptions list (without specific ID filter)
  const {
    data: subscriptionData,
    refetch: categoryListRefetch,
    isLoading,
  } = useQuery({
    queryKey: [
      "subscriptionLists",
      currentCompany,
      searchCategory,
      tenantString,
    ],
    queryFn: () => {
      // ✅ Build query params
      let queryParams = `pageNumber=1`;

      if (!tenantString) {
        queryParams += `&selectedCompany=${currentCompany}`;
      }

      if (searchCategory) {
        queryParams += `&search=${searchCategory}`;
      }

      if (tenantString) {
        queryParams += tenantString;
      }

      console.log("Fetching subscriptions with params:", queryParams);

      return getWithQueryApiServices(
        apiRoutes.GET_SUBSCRIPTION_LIST,
        queryParams,
      );
    },
    enabled: !!currentCompany,
  });

  // ✅ Fetch SINGLE subscription details when selected (optional - for detailed view)
  const { data: subscriptionDetails, refetch: refetchDetails } = useQuery({
    queryKey: ["subscriptionDetails", selectedSubscriptionId],
    queryFn: () => {
      console.log(
        "Fetching subscription details for ID:",
        selectedSubscriptionId,
      );

      let queryParams = `subscriptionID=${selectedSubscriptionId}`;

      if (!tenantString) {
        queryParams += `&selectedCompany=${currentCompany}`;
      }

      if (tenantString) {
        queryParams += tenantString;
      }

      return getWithQueryApiServices(
        apiRoutes.GET_SUBSCRIPTION_LIST,
        queryParams,
      );
    },
    enabled: !!selectedSubscriptionId && !!currentCompany,
  });

  // ✅ Debug logs
  useEffect(() => {
    console.log("subscriptionData:", subscriptionData);
    console.log("selectedSubscriptionId:", selectedSubscriptionId);
    console.log("subscriptionDetails:", subscriptionDetails);
  }, [subscriptionData, selectedSubscriptionId, subscriptionDetails]);

  // ✅ Handle checkbox selection
  const handleCategoryCheckbox = (item) => {
    console.log("Selected item:", item);

    // ✅ Get subscriptionID from the item
    const subscriptionID = item?.subscriptionID || item?.id;
    console.log("Extracted subscriptionID:", subscriptionID);

    let copyValue = [...selectedCategories];

    if (
      copyValue.some(
        (cat) => (cat?.subscriptionID || cat?.id) === subscriptionID,
      )
    ) {
      // Deselect
      copyValue = copyValue.filter(
        (val) => (val?.subscriptionID || val?.id) !== subscriptionID,
      );
      setSelectedSubscriptionId(null);
    } else {
      // Select - store with guaranteed subscriptionID
      copyValue = [
        {
          ...item,
          subscriptionID: subscriptionID,
        },
      ];
      setSelectedSubscriptionId(subscriptionID);
    }

    setSelectedCategories([...copyValue]);

    if (copyValue?.length === 1) {
      setCategoryData({
        ...copyValue[0],
        subscriptionID: subscriptionID,
      });
      setAddNewCategory(true);
    } else {
      setCategoryData({});
      setAddNewCategory(false);
    }
  };

  // ✅ Handle add new
  const handleAddNew = () => {
    setCategoryData({});
    setSelectedCategories([]);
    setSelectedSubscriptionId(null);
    setAddNewCategory(true);
  };

  // ✅ Reset when company changes
  useEffect(() => {
    if (currentCompany) {
      setAddNewCategory(false);
      setSelectedCategories([]);
      setCompanyForm([]);
      setSearchCategory("");
      setCategoryData({});
      setSelectedSubscriptionId(null);
    }
  }, [currentCompany]);

  // ✅ Check permissions
  useEffect(() => {
    const currentUser = usersData?.data?.userList?.find(
      (item) => item.userID === parseInt(userID),
    );
    if (!!currentTenant) {
      setPermission(true);
    } else {
      setPermission(
        currentUser?.levelList?.includes("Edit Subscription") ? true : false,
      );
    }
  }, [usersData, userID, currentTenant]);

  // ✅ Get rows with guaranteed ID for DataGrid
  const getRowsWithId = () => {
    const list = subscriptionData?.data?.list || subscriptionData?.data || [];
    return list.map((item, index) => ({
      ...item,
      id: item.subscriptionID || item.id || index,
      subscriptionID: item.subscriptionID || item.id || index,
    }));
  };

  return (
    <ParentPageWrapper>
      <BoxContainer>
        <StyledHeader>Subscriptions</StyledHeader>

        {/* {isGlobalSuperAdmin && (
          <Box
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              bgcolor: "#f5f5f5",
              p: 2,
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#666" }}>
              Select Company:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <Select
                value={currentCompany || ""}
                onChange={handleGlobalCompanyChange}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Company
                </MenuItem>
                {fullCompanyList?.map((company) => (
                  <MenuItem key={company.companyID} value={company.companyID}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )} */}

        <ParentContainer>
          <SplitContainer
            style={{
              backgroundColor: "transparent",
              width: "60%",
            }}
          >
            <div
              style={{
                height: "80vh",
              }}
            >
              <div
                style={{
                  backgroundColor: "white",
                  height: "92%",
                  borderRadius: 10,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ flex: 1, minHeight: 0 }}>
                  <FullFeaturedCrudGrid
                    initialRows={getRowsWithId()}
                    columns={columns}
                    label="subscription"
                    handleAddNew={handleAddNew}
                    handleSelection={handleCategoryCheckbox}
                    search={searchCategory}
                    setSearch={setSearchCategory}
                    apiUrl={apiRoutes.GET_SUBSCRIPTION_LIST}
                    refetch={categoryListRefetch}
                    hideEdit={true}
                    initialFormValue={companyFormInitVal}
                    permission={permission}
                    loading={isLoading}
                    getRowId={(row) => row.subscriptionID || row.id}
                    selectedRowId={selectedSubscriptionId || null}
                  />
                </div>
              </div>
            </div>
          </SplitContainer>

          <SplitContainer
            style={{
              backgroundColor: "transparent",
              cursor: addNewCategory ? "auto" : "not-allowed",
            }}
          >
            <SubscriptionForm
              categoryListRefetch={categoryListRefetch}
              companyListRefetch={companyListRefetch}
              companyForm={companyForm}
              setCompanyForm={setCompanyForm}
              categoryData={categoryData}
              setCategoryData={setCategoryData}
              setAddNewCategory={setAddNewCategory}
              addNewCategory={addNewCategory}
              setSelectedCategories={setSelectedCategories}
              setSelectedSubscriptionId={setSelectedSubscriptionId}
              selectedSubscriptionId={selectedSubscriptionId}
              permission={permission}
            />
          </SplitContainer>
        </ParentContainer>
      </BoxContainer>
    </ParentPageWrapper>
  );
};
